(() => {
  'use strict';

  const VERSION = '4.0.0';
  const DATA = () => window.AURYMED_CONFIG_DATA || {};
  const ROOT_SELECTOR = '[data-aurymed-konfigurator]';
  const SURCHARGE_NAMES = [
    'Preis – Zusatzoptionen',
    'Preis - Zusatzoptionen',
    'Preis – Zusatzoption',
    'Preis-Zusatzoptionen'
  ];

  window.AURYMED_CONFIGURATOR_VERSION = VERSION;

  const money = (n) => Number(n || 0).toFixed(2).replace('.', ',') + ' €';
  const round2 = (n) => Math.round((Number(n) + Number.EPSILON) * 100) / 100;

  function normalizedText(value) {
    return String(value || '')
      .replace(/\u00a0/g, ' ')
      .replace(/[–—]/g, '-')
      .replace(/\s+/g, ' ')
      .trim()
      .toLowerCase();
  }

  function isSurchargeName(value) {
    const n = normalizedText(value);
    return SURCHARGE_NAMES.some(name => normalizedText(name) === n);
  }

  // AURYMED: Wenn eine Auswahl inzwischen als echte Shopify-Variante existiert,
  // darf der alte Konfigurator dieselbe Auswahl nicht ein zweites Mal anzeigen.
  // So bleibt genau EIN sichtbarer Selektor die Quelle der Wahrheit und native
  // Variantenpreise (z. B. +3 EUR für einen PLA-Halter) aktualisieren sauber.
  function canonicalOptionName(value) {
    let n = normalizedText(value)
      .replace(/ä/g, 'ae')
      .replace(/ö/g, 'oe')
      .replace(/ü/g, 'ue')
      .replace(/ß/g, 'ss')
      .replace(/[^a-z0-9]+/g, ' ')
      .trim();

    const compact = n.replace(/\s+/g, '');
    if (['farbe', 'farbauswahl', 'farbwahl', 'wunschfarbe'].includes(compact)) return 'farbe';
    if (['plaring', '3ddruckring', '3ddruckpla ring', 'ring'].includes(compact)) return 'plaring';
    if (['plahalter', '3ddruckhalter', 'halter'].includes(compact)) return 'plahalter';
    if (['befestigung', 'tragebefestigungsart', 'kettenbandoderschluesselkarabinerhaken'].includes(compact)) return 'befestigung';
    return compact;
  }

  function nativeVariantOptionNames(root) {
    const scope = productScope(root);
    const names = new Set();
    scope.querySelectorAll('select[name^="options["], input[type="radio"][name^="options["]').forEach(el => {
      const raw = optionNameFromInput(el);
      if (raw) names.add(canonicalOptionName(raw));
    });
    return names;
  }

  function duplicatedByNativeVariant(root, field) {
    if (!field || field.mode === 'native' || field.type === 'text') return false;
    const nativeNames = nativeVariantOptionNames(root);
    if (!nativeNames.size) return false;
    const candidates = [field.name, field.source_name, field.label]
      .filter(Boolean)
      .map(canonicalOptionName);
    return candidates.some(name => nativeNames.has(name));
  }

  function currentProductHandle() {
    const explicit = document.querySelector(ROOT_SELECTOR)?.dataset?.productHandle;
    if (explicit) return explicit;
    const m = location.pathname.match(/\/products\/([^/?#]+)/i);
    return m ? decodeURIComponent(m[1]) : '';
  }

  function configForHandle(handle) {
    if (!handle) return null;
    const store = DATA();
    if (store[handle]) return store[handle];
    const normalized = normalizedText(handle).replace(/\s/g, '');
    const hit = Object.keys(store).find(k =>
      normalizedText(k).replace(/\s/g, '') === normalized
    );
    return hit ? store[hit] : null;
  }

  function hideTechnicalPriceFieldsEverywhere() {
    document.querySelectorAll('select[name^="options["], input[name^="options["]').forEach(el => {
      const optionName = optionNameFromInput(el);
      if (!isSurchargeName(optionName)) return;
      const wrap = internalWrapper(el);
      forceHide(wrap);
      if (el.id) {
        const label = document.querySelector(`label[for="${CSS.escape(el.id)}"]`);
        forceHide(label);
      }
    });

    document.querySelectorAll('label, legend, p, span, div').forEach(el => {
      const txt = (el.textContent || '').trim();
      if (!txt || txt.length > 80 || !isSurchargeName(txt)) return;
      const wrap = el.closest(
        'fieldset.variant-option, .variant-option, .product-form__input, [data-variant-option], fieldset'
      ) || el.parentElement;
      forceHide(wrap || el);
    });
  }

  function ensureConfiguratorRoot() {
    const handle = currentProductHandle();
    const cfg = configForHandle(handle);
    if (!cfg) return null;

    let root = document.querySelector(ROOT_SELECTOR);
    if (root) {
      if (!root.dataset.productHandle) root.dataset.productHandle = handle;
      return root;
    }

    const anchor =
      document.querySelector('product-form, .shopify-product-form') ||
      document.querySelector('variant-picker') ||
      document.querySelector('[class*="buy-buttons"]') ||
      document.querySelector('button[name="add"]')?.parentElement;

    if (!anchor || !anchor.parentElement) return null;

    root = document.createElement('div');
    root.className = 'aurymed-configurator';
    root.dataset.aurymedKonfigurator = '';
    root.dataset.productHandle = handle;
    anchor.parentElement.insertBefore(root, anchor);
    return root;
  }

  function optionNameFromInput(el) {
    const raw = el?.getAttribute?.('name') || '';
    const m = raw.match(/^options\[(.*)\]$/);
    if (m) return m[1];
    return el?.dataset?.optionName || '';
  }

  function parseSurchargeValue(value) {
    const text = normalizedText(value);
    if (!text) return null;
    if (text.includes('kein zusatzpreis')) return 0;
    const m = String(value || '').match(/zusatzpreis\s*\+\s*([0-9]+(?:[.,][0-9]{1,2})?)\s*eur/i);
    return m ? Number(m[1].replace(',', '.')) : null;
  }

  function rootConfig(root) {
    const handle = root?.dataset?.productHandle || currentProductHandle();
    return configForHandle(handle);
  }

  function productScope(root) {
    if (!root) return document;
    return root.closest('[data-section-id], .shopify-section, section') || document;
  }

  function allNativeOptionControls(root) {
    const scope = productScope(root);
    const candidates = Array.from(scope.querySelectorAll(
      'select[name^="options["], input[type="radio"][name^="options["], input[type="radio"][data-option-name]'
    ));
    return candidates.filter(el => isSurchargeName(optionNameFromInput(el)));
  }

  function internalWrapper(el) {
    return el.closest(
      'fieldset.variant-option, .variant-option, .product-form__input, [data-variant-option], [data-option-name], fieldset'
    ) || el.parentElement;
  }

  function forceHide(el) {
    if (!el) return;
    el.setAttribute('aria-hidden', 'true');
    el.hidden = true;
    el.style.setProperty('display', 'none', 'important');
  }

  function hideInternalPriceOption(root) {
    const controls = allNativeOptionControls(root);
    const hidden = new Set();

    controls.forEach(el => {
      const wrap = internalWrapper(el);
      if (wrap && !hidden.has(wrap)) {
        wrap.dataset.aurymedInternalPriceOption = '1';
        forceHide(wrap);
        hidden.add(wrap);
      }

      // Fallback for themes where the label sits outside the option wrapper.
      if (el.id) {
        const label = productScope(root).querySelector(`label[for="${CSS.escape(el.id)}"]`);
        if (label) forceHide(label);
      }
    });
  }

  function selectedCustomSurcharge(root) {
    let total = 0;
    root.querySelectorAll('[data-aury-field][data-priced="1"]').forEach(field => {
      if (field.matches('select')) {
        const opt = field.options[field.selectedIndex];
        total += Number(opt?.dataset?.delta || 0);
      } else if (field.matches('input[type="checkbox"]') && field.checked) {
        total += Number(field.dataset.delta || 0);
      }
    });
    return round2(total);
  }

  function valueExistsForTotal(root, total) {
    const cfg = rootConfig(root);
    const allowed = (cfg?.hidden_surcharge_values || []).map(round2);
    if (!allowed.length) return true;
    return allowed.some(v => Math.abs(v - total) < 0.001);
  }

  function setSurcharge(root) {
    const cfg = rootConfig(root);
    if (!cfg || !cfg.hidden_surcharge_values || !cfg.hidden_surcharge_values.length) return false;

    const total = selectedCustomSurcharge(root);
    if (!valueExistsForTotal(root, total)) {
      console.warn('[AURYMED] Keine passende interne Preisstufe für', total, root.dataset.productHandle);
      return false;
    }

    const controls = allNativeOptionControls(root);
    if (!controls.length) return false;

    let changed = false;
    const select = controls.find(el => el.matches('select'));

    if (select) {
      const target = Array.from(select.options).find(opt => {
        const parsed = parseSurchargeValue(opt.value);
        return parsed !== null && Math.abs(parsed - total) < 0.001;
      });
      if (target && select.value !== target.value) {
        select.value = target.value;
        select.dispatchEvent(new Event('input', { bubbles: true }));
        select.dispatchEvent(new Event('change', { bubbles: true }));
        changed = true;
      }
    } else {
      const radios = controls.filter(el => el.matches('input[type="radio"]'));
      const target = radios.find(el => {
        const parsed = parseSurchargeValue(el.value);
        return parsed !== null && Math.abs(parsed - total) < 0.001;
      });
      if (target && !target.checked) {
        target.checked = true;
        target.dispatchEvent(new Event('input', { bubbles: true }));
        target.dispatchEvent(new Event('change', { bubbles: true }));
        changed = true;
      }
    }

    // Horizon can rebuild the variant picker after a native option change.
    requestAnimationFrame(() => hideInternalPriceOption(root));
    setTimeout(() => hideInternalPriceOption(root), 60);
    return changed;
  }

  function fieldValue(fieldWrap) {
    const type = fieldWrap.dataset.fieldType;
    if (type === 'checkbox') {
      return Array.from(fieldWrap.querySelectorAll('input[type="checkbox"]:checked'))
        .map(x => x.value).join(', ');
    }
    const el = fieldWrap.querySelector('[data-aury-field]');
    return (el?.value || '').trim();
  }

  function validate(root) {
    const errors = [];
    root.querySelectorAll('[data-aury-field-wrap][data-required="1"]').forEach(wrap => {
      if (!fieldValue(wrap)) {
        errors.push(wrap.dataset.fieldLabel || 'Auswahl');
        wrap.classList.add('aurymed-configurator__error');
      } else {
        wrap.classList.remove('aurymed-configurator__error');
      }
    });
    if (errors.length) {
      const first = root.querySelector('.aurymed-configurator__error');
      first?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      alert('Bitte noch auswählen: ' + errors.join(', '));
      return false;
    }
    return true;
  }

  function propertyPairs(root) {
    const pairs = [];
    root.querySelectorAll('[data-aury-field-wrap]').forEach(wrap => {
      const name = (wrap.dataset.propertyName || '').trim();
      const value = fieldValue(wrap);
      if (!name || !value) return;
      pairs.push({ name, value });
    });
    return pairs;
  }

  function productForms(root) {
    const scope = productScope(root);
    let forms = Array.from(scope.querySelectorAll('form[action*="/cart/add"], form.shopify-product-form'));

    // Horizon can render the configurator and the actual product form in sibling wrappers.
    // If the local section lookup misses it, fall back to all product forms on the page.
    if (!forms.length) {
      forms = Array.from(document.querySelectorAll('form[action*="/cart/add"], form.shopify-product-form'));
    }

    return forms.filter((form, index, arr) => arr.indexOf(form) === index);
  }

  function syncPropertiesToForm(root, form) {
    if (!root || !form) return;

    const expected = new Map(
      propertyPairs(root).map(({ name, value }) => [`properties[${name}]`, value])
    );

    const existing = new Map();
    form.querySelectorAll('input[data-aurymed-property="1"]').forEach(input => {
      existing.set(input.name, input);
    });

    expected.forEach((value, name) => {
      const input = existing.get(name);
      if (input) {
        if (input.value !== value) input.value = value;
        existing.delete(name);
        return;
      }

      const hidden = document.createElement('input');
      hidden.type = 'hidden';
      hidden.name = name;
      hidden.value = value;
      hidden.dataset.aurymedProperty = '1';
      form.appendChild(hidden);
    });

    // Remove stale AURYMED properties when the customer changes a choice back/away.
    existing.forEach(input => input.remove());
  }

  function syncProperties(root) {
    productForms(root).forEach(form => syncPropertiesToForm(root, form));
  }

  function injectProperties(root, form) {
    // Kept as a final submit-time safety net. The properties are also kept
    // permanently synced into Horizon's product form because Horizon may add
    // to cart through JavaScript without emitting a normal submit event.
    syncPropertiesToForm(root, form);
  }

  function renderSelect(root, field) {
    const wrap = document.createElement('div');
    wrap.className = 'aurymed-configurator__field';
    wrap.dataset.auryFieldWrap = '1';
    wrap.dataset.fieldType = 'select';
    wrap.dataset.fieldLabel = field.label;
    wrap.dataset.propertyName = field.name;
    wrap.dataset.required = field.required ? '1' : '0';

    const id = 'aury-' + Math.random().toString(36).slice(2);
    const label = document.createElement('label');
    label.htmlFor = id;
    label.innerHTML = `<strong>${field.label}</strong>`;

    const select = document.createElement('select');
    select.id = id;
    select.dataset.auryField = '1';
    if (field.mode === 'custom_priced') select.dataset.priced = '1';

    const first = document.createElement('option');
    first.value = '';
    first.textContent = 'Bitte wählen';
    select.appendChild(first);

    field.values.forEach(v => {
      const o = document.createElement('option');
      o.value = v.label;
      o.dataset.delta = String(v.delta || 0);
      o.textContent = v.label + ((field.mode === 'custom_priced' && Number(v.delta)) ? ` (+${money(v.delta)})` : '');
      select.appendChild(o);
    });

    wrap.append(label, select);
    return wrap;
  }

  function renderText(field) {
    const wrap = document.createElement('div');
    wrap.className = 'aurymed-configurator__field';
    wrap.dataset.auryFieldWrap = '1';
    wrap.dataset.fieldType = 'text';
    wrap.dataset.fieldLabel = field.label;
    wrap.dataset.propertyName = field.name;
    wrap.dataset.required = field.required ? '1' : '0';

    const id = 'aury-' + Math.random().toString(36).slice(2);
    const label = document.createElement('label');
    label.htmlFor = id;
    label.innerHTML = `<strong>${field.label}</strong>`;

    const input = document.createElement('input');
    input.id = id;
    input.type = 'text';
    input.dataset.auryField = '1';
    input.placeholder = 'Bitte eingeben';

    wrap.append(label, input);
    return wrap;
  }

  function renderCheckboxes(field) {
    const wrap = document.createElement('fieldset');
    wrap.className = 'aurymed-configurator__field aurymed-configurator__checks';
    wrap.dataset.auryFieldWrap = '1';
    wrap.dataset.fieldType = 'checkbox';
    wrap.dataset.fieldLabel = field.label;
    wrap.dataset.propertyName = field.name;
    wrap.dataset.required = field.required ? '1' : '0';

    const legend = document.createElement('legend');
    legend.innerHTML = `<strong>${field.label}</strong>`;
    wrap.appendChild(legend);

    field.values.forEach(v => {
      const row = document.createElement('label');
      row.className = 'aurymed-configurator__check';

      const input = document.createElement('input');
      input.type = 'checkbox';
      input.value = v.label;
      input.dataset.auryField = '1';
      input.dataset.delta = String(v.delta || 0);
      if (field.mode === 'custom_priced') input.dataset.priced = '1';

      const span = document.createElement('span');
      span.textContent = v.label + ((field.mode === 'custom_priced' && Number(v.delta)) ? ` (+${money(v.delta)})` : '');

      row.append(input, span);
      wrap.appendChild(row);
    });

    return wrap;
  }

  function renderRoot(root) {
    const cfg = rootConfig(root);
    if (!cfg) {
      root.hidden = true;
      return;
    }

    root.hidden = false;
    root.innerHTML = '';

    const custom = cfg.fields.filter(f => f.mode !== 'native' && !duplicatedByNativeVariant(root, f));
    if (!custom.length) {
      root.hidden = true;
      hideInternalPriceOption(root);
      return;
    }

    const box = document.createElement('div');
    box.className = 'aurymed-configurator__box';

    custom.forEach(field => {
      let node;
      if (field.type === 'checkbox') node = renderCheckboxes(field);
      else if (field.type === 'text') node = renderText(field);
      else node = renderSelect(root, field);
      box.appendChild(node);
    });

    root.appendChild(box);

    const onChoiceChanged = () => {
      setSurcharge(root);
      hideInternalPriceOption(root);
      syncProperties(root);
      root.querySelectorAll('.aurymed-configurator__error').forEach(w => {
        if (fieldValue(w)) w.classList.remove('aurymed-configurator__error');
      });
    };

    root.addEventListener('change', onChoiceChanged);
    root.addEventListener('input', event => {
      if (event.target?.matches?.('[data-aury-field][data-priced="1"]')) onChoiceChanged();
    });

    hideInternalPriceOption(root);
    setSurcharge(root);
    syncProperties(root);
    setTimeout(() => {
      hideInternalPriceOption(root);
      setSurcharge(root);
      syncProperties(root);
    }, 80);

    // Configurable products must use the normal add-to-cart path so properties are not skipped.
    document.querySelectorAll('shopify-accelerated-checkout, .shopify-payment-button').forEach(el => {
      el.style.setProperty('display', 'none', 'important');
    });
    document.querySelectorAll('sticky-add-to-cart').forEach(el => {
      el.style.setProperty('display', 'none', 'important');
    });
  }

  function cleanTechnicalPriceText(container) {
    if (!container) return;

    const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT);
    const nodes = [];
    while (walker.nextNode()) nodes.push(walker.currentNode);

    nodes.forEach(node => {
      const original = node.nodeValue || '';
      if (!/zusatzpreis|kein\s+zusatzpreis/i.test(original)) return;

      // Never touch the actual variant picker controls/options.
      if (node.parentElement?.closest?.('select, option, variant-picker, [data-aurymed-konfigurator]')) return;

      const cleaned = original
        .replace(/\s*,?\s*Zusatzpreis\s*\+\s*[0-9]+(?:[.,][0-9]{1,2})?\s*EUR\s*/gi, '')
        .replace(/\s*,?\s*Kein\s+Zusatzpreis\s*/gi, '')
        .replace(/\s+,/g, ',')
        .replace(/,\s*,/g, ',')
        .replace(/^\s*,\s*|\s*,\s*$/g, '')
        .trim();

      if (cleaned !== original.trim()) node.nodeValue = cleaned;
    });
  }

  function cleanCartPresentation() {
    const selectors = [
      'cart-drawer',
      'cart-items',
      '[id*="CartDrawer"]',
      '[class*="cart-drawer"]',
      '[data-cart-drawer]'
    ];
    document.querySelectorAll(selectors.join(',')).forEach(cleanTechnicalPriceText);

    if (location.pathname === '/cart' || location.pathname.startsWith('/cart?')) {
      cleanTechnicalPriceText(document.querySelector('main') || document.body);
    }
  }

  let housekeepingQueued = false;
  function housekeeping() {
    if (housekeepingQueued) return;
    housekeepingQueued = true;
    requestAnimationFrame(() => {
      housekeepingQueued = false;
      document.querySelectorAll(ROOT_SELECTOR).forEach(root => {
        if (rootConfig(root)) {
          hideInternalPriceOption(root);
          syncProperties(root);
        }
      });
      hideTechnicalPriceFieldsEverywhere();
      cleanCartPresentation();
    });
  }

  function init() {
    ensureConfiguratorRoot();
    hideTechnicalPriceFieldsEverywhere();
    document.querySelectorAll(ROOT_SELECTOR).forEach(root => {
      const handle = root.dataset.productHandle || '';
      if (root.dataset.aurymedReady === '1' && root.dataset.aurymedReadyHandle === handle) {
        hideInternalPriceOption(root);
        return;
      }
      root.dataset.aurymedReady = '1';
      root.dataset.aurymedReadyHandle = handle;
      renderRoot(root);
    });
    housekeeping();
  }

  // Horizon can use a JavaScript add-to-cart path that does not emit a normal
  // submit event. Sync and validate on the add-to-cart click as well.
  document.addEventListener('click', event => {
    const button = event.target?.closest?.(
      'button[type="submit"], input[type="submit"], [name="add"], [data-add-to-cart]'
    );
    if (!button) return;

    const form = button.form || button.closest?.('form[action*="/cart/add"], form.shopify-product-form');
    if (!form) return;

    const section = form.closest?.('[data-section-id], .shopify-section, section');
    const root = section?.querySelector?.(`${ROOT_SELECTOR}:not([hidden])`)
      || document.querySelector(`${ROOT_SELECTOR}:not([hidden])`);
    if (!root || !rootConfig(root)) return;

    setSurcharge(root);
    hideInternalPriceOption(root);
    syncPropertiesToForm(root, form);

    if (!validate(root)) {
      event.preventDefault();
      event.stopImmediatePropagation();
    }
  }, true);

  // Capture Horizon's product form submission before its AJAX handler.
  document.addEventListener('submit', event => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    if (!form.matches('form[action*="/cart/add"], .shopify-product-form')) return;

    const root = document.querySelector(`${ROOT_SELECTOR}:not([hidden])`);
    if (!root || !rootConfig(root)) return;

    setSurcharge(root);
    hideInternalPriceOption(root);

    if (!validate(root)) {
      event.preventDefault();
      event.stopImmediatePropagation();
      return;
    }

    injectProperties(root, form);
  }, true);

  // Horizon may rebuild the native picker/cart drawer after changes.
  const observer = new MutationObserver(() => {
    init();
    housekeeping();
  });

  function startObserver() {
    observer.observe(document.documentElement, { childList: true, subtree: true });
  }

  document.addEventListener('DOMContentLoaded', () => {
    init();
    startObserver();
  });
  document.addEventListener('shopify:section:load', () => setTimeout(init, 0));
  document.addEventListener('shopify:block:select', () => setTimeout(init, 0));
  document.addEventListener('change', housekeeping, true);

  if (document.readyState !== 'loading') {
    init();
    startObserver();
  }
})();
